import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Download, Loader2, Package } from 'lucide-react';

const CONTENTS = [
  ['trial_balance', 'Trial Balance (XLSX+PDF)'],
  ['general_ledger', 'General Ledger full year (XLSX+PDF)'],
  ['bs_pnl_prev_year', 'BS + P&L prev-year comparison (XLSX+PDF)'],
  ['ar_ap_aging', 'A/R + A/P Aging Detail at year-end (XLSX)'],
  ['reconciliations', 'Each bank/CC reconciliation in the year (PDF)'],
  ['1099', '1099 Summary + Detail (XLSX)'],
  ['journal', 'Journal (XLSX)'],
  ['coa_lists_iif', 'Chart of Accounts + lists (CSV) + IIF'],
  ['manifest', 'manifest.json'],
];

export default function YearEnd({ base, isDark = true }) {
  const [packages, setPackages] = useState([]);
  const [fy, setFy] = useState(String(new Date().getFullYear() - 1));
  const [basis, setBasis] = useState('accrual');
  const [setClosing, setSetClosing] = useState(false);
  const [picked, setPicked] = useState(CONTENTS.map((c) => c[0]));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const panel = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';

  const load = async () => { try { const r = await axios.get(`${base}/export-packages`); setPackages(r.data || []); } catch { /* */ } };
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [base]);

  const generate = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(`${base}/export-packages`, { fiscal_year: Number(fy), basis, set_closing_date: setClosing, contents: picked });
      await load();
    } catch (e) { setError(e?.response?.data?.detail || 'Failed to start'); }
    finally { setBusy(false); }
  };
  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const download = (pid) => window.open(`${base}/export-packages/${pid}/download`, '_blank');

  return (
    <div className="space-y-4" data-testid="year-end">
      <Card className={panel}>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Package className="h-4 w-4" /> Year-End Accountant Package</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex gap-4 items-center flex-wrap">
            <label className="flex items-center gap-2">Fiscal year <Input data-testid="ye-fy" value={fy} onChange={(e) => setFy(e.target.value)} className="w-24" /></label>
            <label className="flex items-center gap-2">Basis
              <select data-testid="ye-basis" value={basis} onChange={(e) => setBasis(e.target.value)} className={`rounded px-2 py-1 border ${panel}`}>
                <option value="accrual">Accrual</option><option value="cash">Cash</option><option value="both">Both</option>
              </select>
            </label>
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="ye-set-closing" checked={setClosing} onChange={(e) => setSetClosing(e.target.checked)} /> Set closing date to year-end after export</label>
          </div>
          <div className="grid grid-cols-2 gap-1">
            {CONTENTS.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2"><input type="checkbox" data-testid={`ye-content-${k}`} checked={picked.includes(k)} onChange={() => toggle(k)} /> {label}</label>
            ))}
          </div>
          <Button data-testid="ye-generate-btn" onClick={generate} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Generate Package</Button>
          {error && <div className="text-red-500" data-testid="ye-error">{error}</div>}
        </CardContent>
      </Card>

      <Card className={panel}>
        <CardHeader><CardTitle className="text-base">Packages</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-xs" data-testid="ye-packages">
            <thead><tr className={sub}><th className="text-left">FY</th><th className="text-left">Basis</th><th className="text-left">Status</th><th className="text-left">Files</th><th /></tr></thead>
            <tbody>
              {packages.map((p) => (
                <tr key={p.pid} data-testid={`ye-pkg-${p.pid}`}>
                  <td className="py-1">{p.fiscal_year}</td>
                  <td>{p.basis}</td>
                  <td className={p.status === 'done' ? 'text-emerald-500' : p.status === 'failed' ? 'text-red-500' : 'text-amber-400'}>{p.status}{p.error ? `: ${p.error}` : ''}</td>
                  <td>{(p.files || []).length}</td>
                  <td>{p.status === 'done' && <Button size="sm" variant="outline" data-testid={`ye-download-${p.pid}`} onClick={() => download(p.pid)}><Download className="h-3 w-3 mr-1" />Zip</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
